/* eslint-disable react-refresh/only-export-components -- entry point, no exports needed */
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import App from './App';
import './index.css';

// Initialize i18n
import './i18n';

// Register Service Worker for PWA (auto-managed by vite-plugin-pwa)
import { registerSW } from 'virtual:pwa-register';
import { logger } from './lib/logger';

const updateSW = registerSW({
  onNeedRefresh() {
    updateSW(true);
  },
  onOfflineReady() {
    logger.info('App ready for offline use');
  },
  onRegisteredSW(_swUrl, registration) {
    // Check for SW updates every hour
    if (registration) {
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);
    }
  },
});

// Create a client for TanStack Query with offline-first strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 24 * 60 * 60 * 1000, // 24 hours (previously cacheTime)
      // Use cached data while fetching fresh data
      refetchOnMount: true,
      // Show stale data immediately, fetch in background
      placeholderData: (previousData: unknown) => previousData,
      // CRITICAL: Don't throw errors to ErrorBoundary - handle gracefully
      throwOnError: false,
      // Use offline-first network mode - use cache when offline
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Mutations should pause when offline
      networkMode: 'offlineFirst',
    },
  },
});

// Create localStorage persister for offline cache
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'YIM_QUERY_CACHE',
  // Throttle writes to localStorage
  throttleTime: 1000,
  // Serialize/deserialize functions
  serialize: (data) => JSON.stringify(data),
  deserialize: (data) => JSON.parse(data),
});

// Loading fallback for i18n suspense
function I18nLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 dark:text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // Max age of persisted cache: 24 hours
        maxAge: 24 * 60 * 60 * 1000,
        // Only persist successful queries
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return query.state.status === 'success';
          },
        },
      }}
      onSuccess={() => {
        // Resume any paused mutations after hydration
        queryClient.resumePausedMutations();
      }}
    >
      <Suspense fallback={<I18nLoader />}>
        <App />
      </Suspense>
    </PersistQueryClientProvider>
  </StrictMode>
);
